import * as React from 'react';
import { useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Checkout } from '../../models/Checkout';
import { useAppContext } from '@app/react-common/AppContext';
import { ICheckoutActions } from '../../models/ICheckoutActions';
import { RuleEngineV2Viewer } from '@app/react-common/components/rule-engine-v2/RuleEngineV2Viewer';
import { CheckoutStatusDots } from '@app/react-common/components/checkout-status/CheckoutStatusDots';
import { getCheckoutStatus } from '@app/react-common/utils/CheckoutUtils';

interface GroupsPrioritizationModalProps {
  checkouts: Checkout[];
  checkoutActions: ICheckoutActions;
  fetchData: () => void;
  isOpen: boolean;
  onClose: () => void;
  runningAbTestCheckoutIds: number[];
}

export const GroupsPrioritizationModal: React.FC<GroupsPrioritizationModalProps> = (
  props: GroupsPrioritizationModalProps,
) => {
  const { showToast } = useAppContext();
  const [orderedCheckouts, setOrderedCheckouts] = useState<Checkout[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [expandedCheckoutId, setExpandedCheckoutId] = useState<number | null>(null);
  const [isHoveringStatus, setIsHoveringStatus] = useState<boolean>(false);

  useEffect(() => {
    if (props.checkouts && props.checkouts.length > 0) {
      setOrderedCheckouts([...props.checkouts]);
    }
  }, [props.checkouts, props.isOpen]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === 0) {
      return;
    }

    const items = Array.from(orderedCheckouts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedCheckouts(items);
  };

  const handleClose = () => {
    setExpandedCheckoutId(null);
    props.onClose();
  };

  const toggleCheckoutExpansion = (checkoutId: number) => {
    setExpandedCheckoutId((current) => (current === checkoutId ? null : checkoutId));
  };

  const getTotalRulesCount = (checkout: Checkout): number => {
    if (!checkout.checkoutShippingMethodGroupSegments) {
      return 0;
    }
    return checkout.checkoutShippingMethodGroupSegments.reduce((total, segment) => {
      if (!segment.rules || !segment.rules.ruleGroups) {
        return total;
      }
      const segmentRulesCount = segment.rules.ruleGroups.reduce((groupTotal, ruleGroup) => {
        return groupTotal + (ruleGroup.rules?.length || 0);
      }, 0);
      return total + segmentRulesCount;
    }, 0);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatePromises = orderedCheckouts.map((checkout, index) => {
        const newPriority = index;
        if (checkout.priority !== newPriority) {
          return props.checkoutActions.updateCheckoutPriority(checkout.id, newPriority);
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);

      showToast({ message: 'checkout groups prioritization saved', mode: 'info' });
      props.fetchData();
      props.onClose();
    } catch (error) {
      showToast({ message: 'failed to update prioritization', mode: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={props.isOpen} onClose={handleClose}>
      <div className="groups-prioritization-modal">
        <h2 className="groups-prioritization-modal__title">Prioritize checkout groups</h2>
        <div className="groups-prioritization-modal__info-box">
          <i className="icon icon-more_information" />
          When a buyer qualifies for multiple checkout groups, the one highest on the list is used.
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="checkouts">
            {(provided) => (
              <div className="groups-prioritization-modal__list" {...provided.droppableProps} ref={provided.innerRef}>
                {orderedCheckouts.map((checkout, index) => {
                  const rulesCount = getTotalRulesCount(checkout);
                  const isExpanded = expandedCheckoutId === checkout.id;

                  const draggableContent = (
                    <div className="groups-prioritization-modal__list__item-wrapper">
                      <span className="groups-prioritization-modal__list__item__number">{index + 1}</span>
                      <div
                        className={`groups-prioritization-modal__list__item disabled ${
                          isHoveringStatus ? 'status-hovered' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCheckoutExpansion(checkout.id);
                        }}>
                        {index !== 0 && (
                          <div className="groups-prioritization-modal__list__item__drag-handle">
                            <i className="icon icon-hamburger" />
                          </div>
                        )}
                        <div className="groups-prioritization-modal__list__item__content">
                          <span className="groups-prioritization-modal__list__item__name">{checkout.name}</span>
                          <div className="groups-prioritization-modal__list__item__right-content">
                            <div
                              className="groups-prioritization-modal__list__item__status-first"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onMouseEnter={() => setIsHoveringStatus(true)}
                              onMouseLeave={() => setIsHoveringStatus(false)}>
                              <CheckoutStatusDots
                                status={getCheckoutStatus(checkout, props.runningAbTestCheckoutIds)}
                              />
                            </div>
                            {index !== 0 && (
                              <>
                                {isExpanded && rulesCount > 0 ? (
                                  <span className="groups-prioritization-modal__list__item__rules-text">
                                    Hide rules
                                  </span>
                                ) : (
                                  <span className="groups-prioritization-modal__list__item__rules-text">
                                    {rulesCount} {rulesCount === 1 ? 'rule' : 'rules'}
                                  </span>
                                )}
                              </>
                            )}
                            {isExpanded ? (
                              <i className="icon icon-chevron-up groups-prioritization-modal__list__item__chevron" />
                            ) : (
                              <i className="icon icon-chevron-down groups-prioritization-modal__list__item__chevron" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );

                  if (index === 0) {
                    return (
                      <div key={checkout.id.toString()}>
                        {draggableContent}
                        {expandedCheckoutId === checkout.id &&
                          checkout.checkoutShippingMethodGroupSegments &&
                          checkout.checkoutShippingMethodGroupSegments.length > 0 && (
                            <div className="groups-prioritization-modal__list__expanded-segments">
                              <RuleEngineV2Viewer
                                segments={checkout.checkoutShippingMethodGroupSegments}
                                checkoutId={checkout.id}
                              />
                            </div>
                          )}
                      </div>
                    );
                  }

                  return (
                    <React.Fragment key={checkout.id.toString()}>
                      <Draggable draggableId={checkout.id.toString()} index={index}>
                        {(provided, snapshot) => (
                          <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                            className={`groups-prioritization-modal__list__item-wrapper ${
                              snapshot.isDragging ? 'dragging' : ''
                            }`}>
                            <span className="groups-prioritization-modal__list__item__number">{index + 1}</span>
                            <div
                              className={`groups-prioritization-modal__list__item ${
                                snapshot.isDragging ? 'dragging' : ''
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCheckoutExpansion(checkout.id);
                              }}>
                              <div className="groups-prioritization-modal__list__item__drag-handle">
                                <i className="icon icon-hamburger" />
                              </div>
                              <div className="groups-prioritization-modal__list__item__content">
                                <span className="groups-prioritization-modal__list__item__name">{checkout.name}</span>
                                <div className="groups-prioritization-modal__list__item__right-content">
                                  <div className="groups-prioritization-modal__list__item__status">
                                    <CheckoutStatusDots
                                      status={getCheckoutStatus(checkout, props.runningAbTestCheckoutIds)}
                                    />
                                  </div>
                                  {isExpanded && rulesCount > 0 ? (
                                    <span className="groups-prioritization-modal__list__item__rules-text">
                                      Hide rules
                                    </span>
                                  ) : (
                                    <span className="groups-prioritization-modal__list__item__rules-text">
                                      {rulesCount} {rulesCount === 1 ? 'rule' : 'rules'}
                                    </span>
                                  )}
                                  {isExpanded ? (
                                    <i className="icon icon-chevron-up groups-prioritization-modal__list__item__chevron" />
                                  ) : (
                                    <i className="icon icon-chevron-down groups-prioritization-modal__list__item__chevron" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                      {expandedCheckoutId === checkout.id &&
                        checkout.checkoutShippingMethodGroupSegments &&
                        checkout.checkoutShippingMethodGroupSegments.length > 0 && (
                          <div className="groups-prioritization-modal__list__expanded-segments">
                            <RuleEngineV2Viewer
                              segments={checkout.checkoutShippingMethodGroupSegments}
                              checkoutId={checkout.id}
                            />
                          </div>
                        )}
                    </React.Fragment>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div className="groups-prioritization-modal__actions">
        <Button
          onClick={handleClose}
          text={'Cancel'}
          size={'medium'}
          variant={'secondary'}
          className="groups-prioritization-modal__actions__cancel"
        />
        <Button onClick={handleSave} text={'Save'} size={'medium'} variant={'primary'} disabled={isSaving} />
      </div>
    </Modal>
  );
};
